import cn from 'classnames'
import { useEffect, useMemo, useRef, useState } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import {
  AbsolutePopup,
  type IPopupMethods,
  PopPaper,
  TPopupHorizontal,
  TPopupVertical,
} from 'core-components/library/popup'
import { Tooltip } from 'core-components/library/tooltip'

import type { OptionalToggle } from '../lib/getOptionalToggle'
import { CommitmentTypes } from '../model/enums'
import type { AcknowledgmentCommitmentItem } from '../model/types'
import { ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS } from '../translations'

import styles from './AcknowledgmentSecondaryActionsMenu.module.scss'

type AcknowledgmentSecondaryActionsMenuProps = {
  item: AcknowledgmentCommitmentItem
  isSubmitting: boolean
  onRequestRefuse: (item: AcknowledgmentCommitmentItem) => void
  onRequestDrop: (item: AcknowledgmentCommitmentItem) => void
  onRequestChangeSigner: (item: AcknowledgmentCommitmentItem) => void
  onRequestRemove: (item: AcknowledgmentCommitmentItem) => void
  onConfirmManager: (item: AcknowledgmentCommitmentItem) => void
  optionalToggle: OptionalToggle | null
  onSetOptional: (item: AcknowledgmentCommitmentItem, isOptional: boolean) => void
  /** Absent when the reader may not simulate. */
  onSimulate?: (item: AcknowledgmentCommitmentItem) => void
}

type MenuAction = {
  id: string
  translationKey: string
  disabled: boolean
  icon?: string
  marker?: boolean
  danger?: boolean
  reasonKey?: string
  onClick: () => void
}

const getChangeActionTranslationKey = (item: AcknowledgmentCommitmentItem) => {
  if (item.CommitmentType === CommitmentTypes.Manager) {
    return item.CanChange !== 0 ? 'jbp-commitment-manager-change' : 'jbp-commitment-manager-select'
  }

  if (item.CommitmentType === CommitmentTypes.PBM) {
    return 'jbp-commitment-pbm-change'
  }

  return item.CanChange !== 0
    ? 'partnercommitment-change-contact'
    : 'partnercommitment-select-contact'
}

export const AcknowledgmentSecondaryActionsMenu = ({
  item,
  isSubmitting,
  onRequestRefuse,
  onRequestDrop,
  onRequestChangeSigner,
  onRequestRemove,
  onConfirmManager,
  optionalToggle,
  onSetOptional,
  onSimulate,
}: AcknowledgmentSecondaryActionsMenuProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const popupRef = useRef<IPopupMethods | null>(null)

  const actions = useMemo<MenuAction[]>(() => {
    const nextActions: MenuAction[] = []

    if (onSimulate && item.SignerEmail) {
      nextActions.push({
        id: 'simulate',
        icon: 'kbase kbase-eye',
        translationKey: ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.common.simulate,
        disabled: false,
        onClick: () => onSimulate(item),
      })
    }

    if (item.CommitmentType !== CommitmentTypes.PBM && item.CanRefuse !== 0) {
      nextActions.push({
        id: 'refuse',
        icon: 'kbase kbase-close-circle',
        translationKey: 'partnercommitment-refuse',
        disabled: item.CanRefuse === -1,
        danger: true,
        onClick: () => onRequestRefuse(item),
      })
    }

    if (item.CanDrop !== 0) {
      nextActions.push({
        id: 'drop',
        icon: 'kbase kbase-undo',
        translationKey: 'partnercommitment-drop-sign',
        disabled: item.CanDrop === -1,
        danger: true,
        onClick: () => onRequestDrop(item),
      })
    }

    if (item.CanChange !== 0 || item.CanSelect !== 0) {
      const isChangeAction = item.CanChange !== 0

      nextActions.push({
        id: 'change',
        icon: 'kbase kbase-user-editor',
        translationKey: getChangeActionTranslationKey(item),
        disabled: isChangeAction ? item.CanChange === -1 : item.CanSelect === -1,
        onClick: () => onRequestChangeSigner(item),
      })
    }

    if (optionalToggle) {
      nextActions.push({
        id: 'optional',
        icon: optionalToggle.isOptional ? 'kbase kbase-lvl-recording' : undefined,
        marker: !optionalToggle.isOptional,
        translationKey: optionalToggle.isOptional
          ? ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.common.markAsRequired
          : ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.common.markAsOptional,
        disabled: optionalToggle.disabled,
        reasonKey: optionalToggle.reasonKey,
        onClick: () => onSetOptional(item, !optionalToggle.isOptional),
      })
    }

    if (item.CommitmentType === CommitmentTypes.Manager && Boolean(item.CanConfirmManager)) {
      nextActions.push({
        id: 'confirm-manager',
        icon: 'kbase kbase-check-circle',
        translationKey: 'jbp-commitment-confirm-manager',
        disabled: item.CanConfirmManager === -1,
        onClick: () => onConfirmManager(item),
      })
    }

    if (item.CanRemove !== 0) {
      nextActions.push({
        id: 'remove',
        icon: 'kbase kbase-delete',
        translationKey: 'jbp-commitment-delete',
        disabled: item.CanRemove === -1,
        danger: true,
        onClick: () => onRequestRemove(item),
      })
    }

    return nextActions
  }, [
    item,
    onConfirmManager,
    onRequestChangeSigner,
    onRequestDrop,
    onRequestRefuse,
    onRequestRemove,
    onSetOptional,
    onSimulate,
    optionalToggle,
  ])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        popupRef.current?.close()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  useEffect(() => {
    if (isSubmitting) {
      popupRef.current?.close()
    }
  }, [isSubmitting])

  if (actions.length === 0) {
    return null
  }

  return (
    <AbsolutePopup
      ref={popupRef}
      rootClassName={styles.menuWrapper}
      verticalStartPosition={TPopupVertical.Bottom}
      horizontalStartPosition={TPopupHorizontal.Right}
      horizontalDirection={TPopupHorizontal.Left}
      verticalOffset={6}
      visibilityCallback={(visible) => {
        const next = visible && !isSubmitting
        setIsOpen(next)
        return next
      }}
      popupContent={({ action }) => (
        <PopPaper className={styles.menuPaper}>
          <div className={styles.menu} role="menu">
            {actions.map((menuAction) => {
              const button = (
                <button
                  type="button"
                  role="menuitem"
                  className={cn(styles.menuItem, {
                    [styles.menuItemDanger]: menuAction.danger,
                  })}
                  disabled={isSubmitting || menuAction.disabled}
                  onClick={() => {
                    action.close()
                    menuAction.onClick()
                  }}
                >
                  <span className={styles.menuIcon} aria-hidden="true">
                    {menuAction.marker ? (
                      <span className={styles.participationMarker} />
                    ) : (
                      <i className={menuAction.icon} />
                    )}
                  </span>
                  <Translation text={menuAction.translationKey} span />
                </button>
              )
              const reasonKey = menuAction.reasonKey
              return reasonKey ? (
                <Tooltip key={menuAction.id} tipContent={() => <Translation text={reasonKey} />}>
                  <span>{button}</span>
                </Tooltip>
              ) : (
                <span key={menuAction.id}>{button}</span>
              )
            })}
          </div>
        </PopPaper>
      )}
    >
      <button
        type="button"
        className={styles.menuTrigger}
        disabled={isSubmitting}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <span>
          <Translation
            text={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.common.actionMenu}
            span
          />
        </span>
        <i className="kbase kbase-chevron-bottom" aria-hidden="true" />
      </button>
    </AbsolutePopup>
  )
}
